import Widget from "./Widget";
import { gql } from "@apollo/client";
import PinsOutlineIcon from "@src/lib/icons/18px/pins-outline";
import { useLocationAssignmentLeaderboardWidgetQuery } from "./LocationAssignmentLeaderboardWidget.generated";
import { LeaderboardWindow } from "@src/types.generated";
import { useMemo } from "react";
import {
  HtmlTable,
  HtmlTableBody,
  HtmlTableCell,
  HtmlTableHead,
  HtmlTableHeader,
  HtmlTableRow,
} from "@src/lib/ui/HtmlTable";
import Text from "@src/lib/ui/Text";
import { Skeleton } from "@src/lib/ui/Skeleton";
import EmptyWidgetContent from "./EmptyWidgetContent";

const getMedalEmoji = (index: number): string => {
  switch (index) {
    case 0:
      return "🥇";
    case 1:
      return "🥈";
    case 2:
      return "🥉";
    default:
      return `${index + 1}`;
  }
};

const LocationAssignmentLeaderboardWidget: React.FC = () => {
  const { data, loading } = useLocationAssignmentLeaderboardWidgetQuery({
    variables: {
      input: {
        window: LeaderboardWindow.AllTime,
        sort: { column: "assignmentsCompleted", descending: true },
      },
    },
  });
  const items = useMemo(() => data?.LocationLeaderboard.objects || [], [data]);
  if (!loading && items.length === 0) {
    return (
      <Widget title="Location Assignment Leaderboard" Icon={PinsOutlineIcon}>
        <EmptyWidgetContent
          illustration={
            <PinsOutlineIcon className="size-8 text-muted-foreground" />
          }
          description="You'll keep track of your top performing locations here"
        />
      </Widget>
    );
  }
  return (
    <Widget
      title="Location Assignment Leaderboard"
      Icon={PinsOutlineIcon}
      href={{ pathname: "/reporting/location-leaderboard" }}
    >
      <div className="mt-4 max-h-[300px] w-full overflow-x-hidden overflow-y-auto">
        {loading && <LocationAssignmentLeaderboardWidgetTableSkeleton />}
        {!loading && items.length > 0 && (
          <HtmlTable className="w-full">
            <HtmlTableHeader>
              <HtmlTableRow>
                <HtmlTableHead className="text-gray-7">Location</HtmlTableHead>
                <HtmlTableHead className="text-right text-gray-7">
                  Completed
                </HtmlTableHead>
                <HtmlTableHead className="text-right text-gray-7">
                  Avg accuracy
                </HtmlTableHead>
              </HtmlTableRow>
            </HtmlTableHeader>
            <HtmlTableBody>
              {items.map((item, index) => (
                <HtmlTableRow key={item.id}>
                  <HtmlTableCell className="flex">
                    <div className="mr-2 w-[20px]">
                      <Text variant="body-description">
                        {getMedalEmoji(index)}
                      </Text>
                    </div>
                    <Text variant="compact-description">
                      {item.locationName}
                    </Text>
                  </HtmlTableCell>
                  <HtmlTableCell className="text-right">
                    <Text variant="compact-description">
                      {item.assignmentsCompleted}
                    </Text>
                  </HtmlTableCell>
                  <HtmlTableCell className="text-right">
                    <Text variant="compact-description">
                      {item.averageAccuracy?.toFixed(0)}%
                    </Text>
                  </HtmlTableCell>
                </HtmlTableRow>
              ))}
            </HtmlTableBody>
          </HtmlTable>
        )}
      </div>
    </Widget>
  );
};

const LocationAssignmentLeaderboardWidgetTableSkeleton: React.FC = () => (
  <HtmlTable className="w-full">
    <HtmlTableHeader>
      <HtmlTableRow>
        <HtmlTableHead className="text-gray-7">Location</HtmlTableHead>
        <HtmlTableHead className="text-right text-gray-7">
          Completed
        </HtmlTableHead>
        <HtmlTableHead className="text-right text-gray-7">
          Avg accuracy
        </HtmlTableHead>
      </HtmlTableRow>
    </HtmlTableHeader>
    <HtmlTableBody>
      {[1, 2, 3, 4, 5].map((index) => (
        <HtmlTableRow key={index}>
          <HtmlTableCell className="flex">
            <div className="mr-2 w-[20px]">
              <Skeleton className="size-[20px] rounded-md" />
            </div>
            <Skeleton className="h-[20px] w-[150px] rounded-md" />
          </HtmlTableCell>
          <HtmlTableCell className="text-right">
            <Skeleton className="ml-auto h-[20px] w-[40px] rounded-md" />
          </HtmlTableCell>
          <HtmlTableCell className="text-right">
            <Skeleton className="ml-auto h-[20px] w-[40px] rounded-md" />
          </HtmlTableCell>
        </HtmlTableRow>
      ))}
    </HtmlTableBody>
  </HtmlTable>
);

gql`
  fragment LocationAssignmentLeaderboardWidgetItem on LocationLeaderboardItem {
    id
    locationId
    locationName
    averageAccuracy
    assignmentsCompleted
    rank
  }
  query LocationAssignmentLeaderboardWidget($input: LocationLeaderboardInput!) {
    LocationLeaderboard(input: $input, pagination: { limit: 5 }) {
      totalCount
      objects {
        ...LocationAssignmentLeaderboardWidgetItem
      }
    }
  }
`;

export default LocationAssignmentLeaderboardWidget;
